import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Fuel, Pipette, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const OilAndGas = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/oil-gas"]?.title} description={seoData["industries/oil-gas"]?.description} path="/industries/oil-gas" keywords={seoData["industries/oil-gas"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Oil & Gas"
        description="Certified industrial coating contractor for oil and gas facilities. BCS provides corrosion repair, pipeline coating, tank lining, and secondary containment coatings for refineries, upstream operations, and midstream infrastructure across California."
        backgroundKey="oil-and-gas"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Pipeline corrosion, pitting, and active leaks under insulation',
              'Tank bottom plate deterioration and product contamination',
              'Pump cavitation damage and impeller erosion',
              'Heat exchanger tube sheet corrosion and fouling',
              'Offshore splash zone corrosion on platforms and risers',
              'Secondary containment failures and EPA compliance gaps'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-amber-50 rounded-lg">
                <Fuel className="text-amber-700 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wrench className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Pipeline Coating & Leak Sealing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Cold-applied composite wraps and epoxy repair systems for live leak sealing on process lines, cooling water pipes, and fire water mains. ISO 24817 / ASME PCC-2 compliant. No hot work permits, no shutdowns required.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wrench className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Storage Tank Lining & Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Interior tank lining with chemical-resistant epoxy systems for crude oil, produced water, and chemical storage tanks. Floor plate rebuild, shell corrosion repair, and exterior coating to extend tank service life by 15-20 years.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wrench className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Rotating Equipment Rebuild
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">In-situ pump restoration including bearing housing rebuild, impeller repair, shaft reconditioning, and volute casing repair using Belzona metal repair composites. Return equipment to OEM tolerances without removal.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Wrench className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Secondary Containment Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Chemical-resistant containment lining systems for bund walls, containment dikes, and spill prevention areas. Epoxy and polyurea coatings that meet EPA 40 CFR 112 SPCC requirements for oil and fuel storage.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Oil & Gas?</h2>
              <div className="space-y-3">
                {['No hot work — cold-applied systems eliminate fire permits and ignition risk in hazardous areas',
                  'In-situ repairs — fix equipment on-site without removal, transport, or crane costs',
                  'Zero production loss — many repairs performed while equipment remains in service',
                  'OSHA and EPA compliant repair methods for classified hazardous locations',
                  'Manufacturer-backed warranties on all installed coating and repair systems',
                  '24/7 emergency response with rapid mobilization for critical leak and spill events'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-amber-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Refinery & Upstream Specialists</h3>
              <p className="text-amber-100 mb-6">BCS has completed 500+ projects across California refineries and upstream operations. Our team is TWIC credentialed, DBIDS cleared, and trained for work in classified hazardous areas.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/pipeline-repair')}>
                View Pipeline Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Oil & Gas Assets</h2>
          <p className="text-blue-100 mb-8">Contact our refinery and upstream specialists for a free site assessment. We'll identify corrosion risks and recommend the most cost-effective repair and protection strategy.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OilAndGas;
